import {
  Box,
  Flex,
  SimpleGrid,
  Text,
  Image as ChakraImg,
} from '@chakra-ui/react';
import Image from 'next/image';
import Link from 'next/link';
import React from 'react';

interface ICardData {
  name: string;
  image: { location: string };
  _id: string;
  position: string;
  slug: string;
  details_link: string;
  designation?: string;
  type: string[];
  nameColor?: string;
  sort?: boolean;
}

const Card = ({
  data,
  jury,
  nameColor = 'text.green',
}: {
  data: ICardData;
  jury?: boolean;
  nameColor?: string;
}) => (
  <Flex maxH="100%" role="group" key={data._id} position="relative" w="100%">
    <Box
      width="100%"
      height={{ xs: '28rem', lg: '25rem', xl: '28rem', '2xl': '50rem' }}
    >
      <Image
        src={data.image.location}
        alt={data.name || 'person image with info'}
        style={{
          objectFit: 'cover',
          filter: 'grayscale(90%)',
        }}
        fill
      />
    </Box>
    <Box
      top="0"
      bottom="0"
      left="0"
      right="0"
      opacity="0"
      _groupHover={{ opacity: '1', transition: '0.5s' }}
      position="absolute"
      bgGradient="linear-gradient(360deg, rgba(14, 14, 44, 0.78) 25.37%, rgba(50, 50, 94, 0.1482) 69.85%)"
    />
    <Flex
      pos="absolute"
      flexDir="column"
      bottom="0"
      px="1rem"
      pb="1rem"
      left="0"
      opacity="0"
      _groupHover={{ opacity: '1', transition: '1s' }}
    >
      <Text fontSize="19px" fontWeight="800" color={nameColor}>
        {data.name}

        {data?.type && data?.type[0] === 'Special Invitee' && (
          <Text as="span" fontSize="12px" fontWeight="400" color="white">
            ( Special Invitee )
          </Text>
        )}
      </Text>
      <Flex maxW="220px">
        <Text
          pr="10px"
          fontSize="14px"
          fontWeight="500"
          color="rgba(255, 255, 255, 0.78)"
          pt="8px"
        >
          {data.position ?? data.designation}
        </Text>
        {!jury && (
          <Image
            src="/svg/galleryArrow.svg"
            alt="arrow"
            width={20}
            height={20}
          />
        )}
      </Flex>
    </Flex>
  </Flex>
);
const GreenReturnsCard = ({ data }: { data: ICardData }) => (
  <Flex direction="column" align="center" w="100%" my="15px">
    <Box
      key={data._id}
      textAlign="center"
      w="270px"
      h="335px"
      bg="white"
      borderRadius="lg"
      //   boxShadow="xl"
      boxShadow="0 4px 6px rgba(25, 117, 66, 0.3)"
      p="1rem"
    >
      <Box mb="1.5rem">
        <ChakraImg
          src={data.image.location}
          alt={data.name || 'speaker image'}
          borderRadius="100%"
          border="2px solid"
          borderColor="green.500"
          boxSize="150px"
          mx="auto"
          objectFit="cover"
        />
      </Box>
      <Text fontSize="20px" fontWeight="bold" mb="0.5rem" color="#197542">
        {data.name}
      </Text>
      <Text fontSize="14px" color="gray.600">
        {data.position ?? data.designation}
      </Text>
    </Box>
  </Flex>
);
interface ISortedCardData extends ICardData {
  name: string;
  image: { location: string };
  _id: string;
  position: string;
  slug: string;
  details_link: string;
  designation?: string;
  type: string[];
  nameColor?: string;
  sortName?: string;
  sort?: boolean;
}

const sortCards = (cards: ICardData[], sort: boolean): ISortedCardData[] =>
  sort
    ? cards
        .map((card) => ({
          ...card,
          sortName: card.name.toLowerCase(),
        }))
        .sort((a, b) => a.sortName.localeCompare(b.sortName))
    : cards;

const GallerySection: React.FC<{
  data: ICardData[];
  slug_pattern?: string;
  nameColor?: string;
  sort?: boolean | any;
  columnNumberOnLg?: number;
}> = ({ data, slug_pattern, nameColor, sort = true, columnNumberOnLg = 4 }) => {
  const sortedData = sortCards(data, sort);

  return (
    <SimpleGrid
      columns={{ xs: 1, sm: 2, md: 3, lg: columnNumberOnLg }}
      w="100%"
      rowGap="4px"
      columnGap="0"
      spacing="0"
      pt={{ xs: '42px', md: '82px' }}
    >
      {sortedData.map((item, id) =>
        slug_pattern === '/committee/member/' ? (
          <Link
            key={id}
            href={`${slug_pattern}${item.slug}`}
            passHref
            aria-label="person image with info"
          >
            <Card data={item} nameColor={nameColor} />
          </Link>
        ) : slug_pattern === '/our-team/' ? (
          <Link
            key={id}
            href={`${slug_pattern}${item.slug}`}
            passHref
            aria-label="person image with info"
          >
            <Card data={item} nameColor={nameColor} />
          </Link>
        ) : slug_pattern === '/greenReturns-2024/speakers/' ? (
          <Link
            key={id}
            href={`${slug_pattern}${item.slug}`}
            passHref
            aria-label="person image with info"
          >
            <GreenReturnsCard data={item} />
          </Link>
        ) : (
          <Card data={item} jury nameColor={nameColor} />
        )
      )}
    </SimpleGrid>
  );
};
export default GallerySection;
